/**
 * The per-device support console — composition only. Right-hand drawer over
 * the Fleet table, opened from a row's "Support" action or via
 * `?unitId=` deep link (`FleetTable.tsx`).
 *
 * RUBY on LIGHT, `bg-white` / `text-gray-900`. Custom modal semantics
 * (`role="dialog"`, `aria-modal`, Escape closes, focus trapped) — no native
 * `confirm()` anywhere, matching `DeviceHistoryModal.tsx`'s pattern.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import type { FleetActionId, FleetCommand } from '@platform/shared-types';
import { FLEET_ACTIONS } from '@platform/shared-types';
import type { FleetRow } from './fleetRows';
import { useDeviceCommands } from './useDeviceCommands';
import DeviceIdentityCard from './DeviceIdentityCard';
import ActionTiers from './ActionTiers';
import CommandHistory from './CommandHistory';
import DestructiveConfirm from './DestructiveConfirm';
import AuditView from './AuditView';
import { toastService } from '../../../services/toastService';

/**
 * The server answers a refused command with a machine code (`conflict('…')`).
 * Surfacing that code raw in a toast makes the operator translate it; these
 * are the sentences a person can act on. Anything unmapped falls through to
 * the server's own message unchanged — never swallowed.
 */
const COMMAND_ERROR_COPY: Array<{ code: string; text: string }> = [
  {
    code: 'auto_update_off',
    text: 'Auto-update is off for this till, so it will not take an update. Turn auto-update back on first, then send it again.',
  },
  {
    code: 'no_target_resolved',
    text: 'No build is resolved for this till yet — it has no pinned version and its ring has no pointer, so there is nothing for "Update now" to install.',
  },
];

export function humaniseCommandError(message?: string | null): string {
  const raw = (message || '').trim();
  if (!raw) return 'Could not send that command';
  const hit = COMMAND_ERROR_COPY.find((c) => raw.includes(c.code));
  return hit ? hit.text : raw;
}

export default function FleetDeviceDrawer({ row, onClose, initialAction }: {
  row: FleetRow;
  onClose: () => void;
  /**
   * Opens straight into that action's confirmation, for entry points that
   * already know which action they want — the row menu's "Re-provision"
   * (Task 9) opens here with `clear_pairing` rather than adding a second
   * unpair-and-reprovision path. Only tier-3 ids do anything: this drawer's
   * tier-1/2 actions have no "open pre-confirmed" state to jump into.
   */
  initialAction?: FleetActionId;
}) {
  const { commands, loading, error, issue } = useDeviceCommands(row.deviceId, row.deviceType);
  const [busyAction, setBusyAction] = useState<FleetActionId | null>(null);
  const [pendingTier3, setPendingTier3] = useState<FleetActionId | null>(
    initialAction && FLEET_ACTIONS[initialAction]?.tier === 3 ? initialAction : null,
  );
  const [showAudit, setShowAudit] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  /** The device's own failure reason from its latest failed attempt at this action, verbatim. */
  const lastFailureFor = (action: FleetActionId): string | null => {
    const last = commands.find((c) => c.action === action && c.state === 'failed');
    return last?.failureReason ?? null;
  };

  // Escape closes — same pattern as `DeviceHistoryModal.tsx`.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      // Minimal focus trap: Tab/Shift+Tab cycle within the drawer.
      if (e.key === 'Tab' && containerRef.current) {
        const focusables = containerRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const latestDeviceReport = useMemo<FleetCommand | undefined>(
    () => commands.find((c) => c.action === 'device_report'),
    [commands],
  );

  const runAction = async (
    action: FleetActionId,
    body?: { params?: Record<string, unknown>; reason?: string; codeId?: string; code?: string },
  ) => {
    if (!row.deviceId || !row.deviceType) {
      toastService.error('This row has no live device to send a command to.');
      return;
    }
    setBusyAction(action);
    try {
      const created = await issue(action, body);
      // Read the SERVER'S state back, never assume success.
      if (created.state === 'queued' && !created.sentAt) {
        toastService.success(`${action.replace(/_/g, ' ')} queued · device offline`);
      } else {
        toastService.success(`${action.replace(/_/g, ' ')} sent`);
      }
    } catch (e: any) {
      toastService.error(humaniseCommandError(e?.message));
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Support console for ${row.registerName || row.deviceId || 'device'}`}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-[720px] flex-col overflow-y-auto border-l-2 border-[#DC143C] bg-white text-gray-900 shadow-2xl outline-none"
      >
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
          <div>
            <div className="text-[15px] font-extrabold text-gray-900">Fleet Device Support</div>
            <div className="text-[11.5px] text-gray-400">Every support action for one till, ordered by what it can destroy</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-4 p-4">
          <DeviceIdentityCard row={row} latestDeviceReport={latestDeviceReport} />

          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
              <h2 className="text-[13px] font-extrabold text-gray-900">Support actions</h2>
              <span className="text-[11.5px] text-gray-400">ordered by what they can destroy</span>
            </div>
            <div className="p-4">
              <ActionTiers
                row={row}
                busyAction={busyAction}
                onRun={(action, params) => runAction(action, params ? { params } : undefined)}
                onRequestDestructive={(action) => setPendingTier3(action)}
              />
            </div>
          </div>

          {error && (
            <div role="alert" className="flex items-start gap-2 rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-[11.5px] font-bold text-rose-700">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              Could not load command history — {error}
            </div>
          )}

          {!loading && <CommandHistory commands={commands} onViewAll={() => setShowAudit(true)} />}
        </div>
      </div>

      {pendingTier3 && (
        <DestructiveConfirm
          row={row}
          action={pendingTier3}
          busy={busyAction === pendingTier3}
          lastFailureReason={lastFailureFor(pendingTier3)}
          onCancel={() => setPendingTier3(null)}
          onConfirm={async (body) => {
            const action = pendingTier3;
            setBusyAction(action);
            try {
              const created = await issue(action, { reason: body.reason, codeId: body.codeId, code: body.code, params: body.params });
              toastService.success(
                created.state === 'queued' && !created.sentAt
                  ? `${action.replace(/_/g, ' ')} queued · device offline`
                  : `${action.replace(/_/g, ' ')} sent`,
              );
              setPendingTier3(null);
            } finally {
              setBusyAction(null);
            }
          }}
        />
      )}

      {showAudit && row.deviceId && (
        <AuditView deviceId={row.deviceId} registerName={row.registerName || row.deviceId} onClose={() => setShowAudit(false)} />
      )}
    </div>
  );
}
